import { Stack, Typography } from '@mui/joy';
import { Trans, useTranslation } from 'react-i18next';

import { Confirmation } from 'core/components';
import { formatDate } from 'core/utils';
import { SpaStatus } from 'quotes/enums';
import { VendorQuoteHelper } from 'quotes/helpers';
import { Color, Level } from 'theme/enums';

interface IProps {
  isOpen: boolean;
  isResend: boolean;
  sentVersion?: number | null;
  // Set only when the quote's Valid Until is before the user's local day.
  pastValidUntil?: string | null;
  // The quote's stored SPA status, which the send writes into the agreement.
  spaStatus?: SpaStatus | null;
  onConfirm: () => void;
  onCancel: () => void;
}

// One dialog per send: a resend always asks, a first send only for a past Valid Until, whose
// warning joins the resend text as an extra row. The next version is not named: the BE decides it
// at send time (it may bump again).
export const SendAsSpaConfirmation = ({
  isOpen,
  isResend,
  sentVersion,
  pastValidUntil,
  spaStatus,
  onConfirm,
  onCancel,
}: IProps) => {
  const { t } = useTranslation();

  const spaStatusLabelKey = VendorQuoteHelper.getSpaStatusLabelKey(spaStatus);

  return (
    <Confirmation
      isOpen={isOpen}
      title={t(isResend ? 'titleResendAsSpa' : 'titleSendAsSpa')}
      message={
        <Stack gap={1}>
          {isResend && (
            <Typography level={Level.BODY_MD}>
              {typeof sentVersion === 'number'
                ? t('messageResendAsSpaConfirmation', { version: sentVersion })
                : t('messageResendAsSpaConfirmationNoVersion')}
            </Typography>
          )}
          {spaStatusLabelKey && (
            <Typography level={Level.BODY_MD}>
              <Trans
                i18nKey="messageSendAsSpaSpaStatus"
                values={{ status: t(spaStatusLabelKey) }}
              />
            </Typography>
          )}
          {pastValidUntil && (
            <Typography level={Level.BODY_MD}>
              <Trans
                i18nKey="messageSendAsSpaPastValidUntil"
                values={{ date: formatDate(pastValidUntil) }}
              />
            </Typography>
          )}
        </Stack>
      }
      color={Color.WARNING}
      confirmBtnLabel={t(isResend ? 'buttonLabelResendAsSpa' : 'buttonLabelSend')}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
};
